import React, { useRef, useState } from 'react';
import { Upload, X, Link2, Loader2 } from 'lucide-react';

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function compressImage(file, maxDim = 1000, quality = 0.78) {
  const dataUrl = await fileToDataUrl(file);
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let { width, height } = img;
      if (width > height && width > maxDim) {
        height = (height * maxDim) / width;
        width = maxDim;
      } else if (height > maxDim) {
        width = (width * maxDim) / height;
        height = maxDim;
      }
      canvas.width = Math.round(width);
      canvas.height = Math.round(height);
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

/** Upload lokal + opsi tempel URL untuk field gambar Page Builder */
export default function ImageUploadField({
  label,
  value = '',
  onChange,
  maxDim = 1000,
}) {
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [showUrl, setShowUrl] = useState(false);
  const [urlDraft, setUrlDraft] = useState('');

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('File harus berupa gambar (JPG, PNG, WEBP, dll.)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Ukuran gambar maksimal 5MB');
      return;
    }
    setLoading(true);
    try {
      const compressed = await compressImage(file, maxDim, 0.78);
      onChange(compressed);
    } finally {
      setLoading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const applyUrl = () => {
    const next = urlDraft.trim();
    if (!next) return;
    onChange(next);
    setShowUrl(false);
    setUrlDraft('');
  };

  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</label>

      {value ? (
        <div className="relative border border-gray-200 rounded-xl p-3 bg-gray-50">
          <img
            src={value}
            alt={label}
            className="w-full max-h-48 object-contain rounded-lg bg-white"
          />
          <div className="flex flex-wrap gap-2 mt-3">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={loading}
              className="flex-1 min-w-[100px] py-2 text-xs font-semibold text-japo-green border border-japo-green rounded-lg hover:bg-rose-50"
            >
              {loading ? 'Memproses...' : 'Ganti (Upload)'}
            </button>
            <button
              type="button"
              onClick={() => { setShowUrl(true); setUrlDraft(value.startsWith('data:') ? '' : value); }}
              className="py-2 px-3 text-xs font-semibold text-gray-700 border border-gray-200 rounded-lg hover:bg-white inline-flex items-center gap-1"
            >
              <Link2 className="w-3.5 h-3.5" /> URL
            </button>
            <button
              type="button"
              onClick={() => onChange('')}
              className="py-2 px-3 text-xs font-semibold text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
              aria-label="Hapus"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={loading}
            className="w-full border-2 border-dashed border-gray-200 rounded-xl p-6 text-center text-sm text-gray-500 hover:border-japo-green hover:bg-rose-50/20 transition-colors flex flex-col items-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-6 h-6 text-japo-green animate-spin" />
                <span>Memproses gambar...</span>
              </>
            ) : (
              <>
                <div className="w-10 h-10 rounded-full bg-japo-green/10 flex items-center justify-center">
                  <Upload className="w-5 h-5 text-japo-green" />
                </div>
                <span className="font-semibold text-gray-700">Upload gambar dari komputer</span>
                <span className="text-xs text-gray-400">PNG, JPG, WEBP · otomatis dikompres</span>
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => setShowUrl(true)}
            className="w-full text-xs font-semibold text-gray-600 hover:text-japo-green py-1.5 inline-flex items-center justify-center gap-1"
          >
            <Link2 className="w-3.5 h-3.5" /> Atau pakai URL gambar
          </button>
        </div>
      )}

      {showUrl && (
        <div className="mt-2 flex gap-2">
          <input
            type="url"
            value={urlDraft}
            onChange={(e) => setUrlDraft(e.target.value)}
            placeholder="https://..."
            className="flex-1 px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-japo-green text-sm"
          />
          <button type="button" onClick={applyUrl} className="px-3 py-2 rounded-lg bg-japo-green text-white text-xs font-semibold">
            Pakai
          </button>
          <button type="button" onClick={() => setShowUrl(false)} className="px-3 py-2 rounded-lg border border-gray-200 text-xs font-semibold">
            Batal
          </button>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleFile}
        className="hidden"
      />
    </div>
  );
}
